import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { AntimalwareAntiTamperingPanelWebComponentSchema } from '../types';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../ui/gzRadio';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';

export class AntimalwareAntiTamperingPanelView extends BaseWebComponentView {
  private formGroup!: FormGroup;

  public dom: {
    // form fields
    $antiTamperingCheckboxForm: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $vulnerableDriversCheckboxForm: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $vulnerableDriversRadioGroupForm: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
    $callbackEvasionCheckboxForm: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $isolateEndpointCheckboxForm: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $rebootEndpointCheckboxForm: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $callbackEvasionRadioGroupForm: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

    // checkboxes
    $vulnerableDriversCheckbox: GzCheckboxWebComponent;
    $callbackEvasionCheckbox: GzCheckboxWebComponent;
    $isolateEndpointCheckbox: GzCheckboxWebComponent;
    $rebootEndpointCheckbox: GzCheckboxWebComponent;

    // titles
    $antiTamperingCheckboxTitle: GzTitleWebComponent;
    $preTamperingTitle: GzTitleWebComponent;
    $postTamperingTitle: GzTitleWebComponent;

    //texts
    $preTamperingText: GzTextWebComponent;
    $postTamperingText: GzTextWebComponent;

    // radios
    $vulnerableDriversActionDenyAccess: GzRadioWebComponent;
    $vulnerableDriversActionRemediate: GzRadioWebComponent;
    $vulnerableDriversActionReportOnly: GzRadioWebComponent;
    $callbackEvasionActionImmediately: GzRadioWebComponent
    $callbackEvasionActionMin5: GzRadioWebComponent
    $callbackEvasionActionMin10: GzRadioWebComponent
    $callbackEvasionActionMin15: GzRadioWebComponent
  }

  getDomElements() {
    this.dom = {
      // form fields
      $antiTamperingCheckboxForm: this.shadow.querySelector('#anti-tampering-checkbox-form'),
      $vulnerableDriversCheckboxForm: this.shadow.querySelector('#vulnerable-drivers-checkbox-form'),
      $vulnerableDriversRadioGroupForm: this.shadow.querySelector('#vulnerable-drivers-radio-group-form'),
      $callbackEvasionCheckboxForm: this.shadow.querySelector('#callback-evasion-checkbox-form'),
      $isolateEndpointCheckboxForm: this.shadow.querySelector('#callback-evasion-actions-isolate-endpoint-checkbox-form'),
      $rebootEndpointCheckboxForm: this.shadow.querySelector('#callback-evasion-actions-reboot-endpoint-checkbox-form'),
      $callbackEvasionRadioGroupForm: this.shadow.querySelector('#callback-evasion-actions-radio-group-form'),

      // checkboxes
      $vulnerableDriversCheckbox: this.shadow.querySelector('#vulnerable-drivers-checkbox'),
      $callbackEvasionCheckbox: this.shadow.querySelector('#callback-evasion-checkbox'),
      $isolateEndpointCheckbox: this.shadow.querySelector('#callback-evasion-actions-isolate-endpoint-checkbox'),
      $rebootEndpointCheckbox: this.shadow.querySelector('#callback-evasion-actions-reboot-endpoint-checkbox'),

      // titles
      $antiTamperingCheckboxTitle: this.shadow.querySelector('#anti-tampering-checkbox-title'),
      $preTamperingTitle: this.shadow.querySelector('#pre-tampering-title'),
      $postTamperingTitle: this.shadow.querySelector('#post-tampering-title'),

      //texts
      $preTamperingText: this.shadow.querySelector('#pre-tampering-text'),
      $postTamperingText: this.shadow.querySelector('#post-tampering-text'),

      // radios
      $vulnerableDriversActionDenyAccess: this.shadow.querySelector('#vulnerable-drivers-action-deny-access'),
      $vulnerableDriversActionRemediate: this.shadow.querySelector('#vulnerable-drivers-action-remediate'),
      $vulnerableDriversActionReportOnly: this.shadow.querySelector('#vulnerable-drivers-action-report-only'),
      $callbackEvasionActionImmediately: this.shadow.querySelector('#callback-evasion-action-immediately'),
      $callbackEvasionActionMin5: this.shadow.querySelector('#callback-evasion-action-5-min'),
      $callbackEvasionActionMin10: this.shadow.querySelector('#callback-evasion-action-10-min'),
      $callbackEvasionActionMin15: this.shadow.querySelector('#callback-evasion-action-15-min'),
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getAntimalwareStrings().policies.antiTampering;
  }

  setTranslatedStrings() {
    // titles
    this.dom.$antiTamperingCheckboxTitle.innerText = this.translations.antiTampering;
    this.dom.$preTamperingTitle.innerText = this.translations.preTampering;
    this.dom.$postTamperingTitle.innerText = this.translations.postTampering;

    // texts
    this.dom.$preTamperingText.innerText = this.translations.preTamperingText;
    this.dom.$postTamperingText.innerText = this.translations.postTamperingText;

    // checkboxes
    this.dom.$vulnerableDriversCheckbox.label = this.translations.vulnerableDrivers;
    this.dom.$callbackEvasionCheckbox.label = this.translations.reportCallbackEvasion;
    this.dom.$isolateEndpointCheckbox.label = this.translations.isolateEndpoint;
    this.dom.$rebootEndpointCheckbox.label = this.translations.rebootEndpoint;

    //radios
    this.dom.$vulnerableDriversActionDenyAccess.label = this.translations.denyAccess;
    this.dom.$vulnerableDriversActionRemediate.label = this.translations.remediate;
    this.dom.$vulnerableDriversActionReportOnly.label = this.translations.reportOnly;
    this.dom.$callbackEvasionActionImmediately.label = this.translations.immediately;
    this.dom.$callbackEvasionActionMin5.label = this.translations.min5;
    this.dom.$callbackEvasionActionMin10.label = this.translations.min10;
    this.dom.$callbackEvasionActionMin15.label = this.translations.min15;
  }

  buildSuccessView(data: AntimalwareAntiTamperingPanelWebComponentSchema) {
    this.createFormGroup();
    this.setFormData(data);
  }

  /**
   * Disable all form
   */
  public setDisabledForm() {
    const antiTamperingCheckboxState = this.dom.$antiTamperingCheckboxForm.Input.checked;

    // disable the two checkboxes if anti-tampering checkbox is unchecked
    this.dom.$vulnerableDriversCheckboxForm.disabled = !antiTamperingCheckboxState;
    this.dom.$callbackEvasionCheckboxForm.disabled = !antiTamperingCheckboxState;

    // propagate disabled state for vulnerable drivers and callback evasion sections
    this.setDisabledVulnerableDrivers();
    this.setDisabledCallbackEvasion();
  }

  /**
   * Disable the Vulnerable Drivers section
   */
  public setDisabledVulnerableDrivers() {
    const antiTamperingCheckboxState = this.dom.$antiTamperingCheckboxForm.Input.checked;
    const vulnerableDriversCheckboxState = this.dom.$vulnerableDriversCheckboxForm.Input.checked;

    // disable the radio group if the anti-tampering or vulnerable drivers checkboxes are unchecked
    this.dom.$vulnerableDriversRadioGroupForm.disabled = !antiTamperingCheckboxState || !vulnerableDriversCheckboxState;
  }

  /**
   * Disable the Callback Evasion section
   */
  public setDisabledCallbackEvasion() {
    const antiTamperingCheckboxState = this.dom.$antiTamperingCheckboxForm.Input.checked;
    const callbackEvasionCheckboxState = this.dom.$callbackEvasionCheckboxForm.Input.checked;

    // disable the two checkboxes if the anti-tampering or callback evasion checkboxes are unchecked
    this.dom.$isolateEndpointCheckboxForm.disabled = !antiTamperingCheckboxState || !callbackEvasionCheckboxState;
    this.dom.$rebootEndpointCheckboxForm.disabled = !antiTamperingCheckboxState || !callbackEvasionCheckboxState;

    // propagate disabled state to reboot time radio group section
    this.setDisabledRebootTimeRadioGroup();
  }

  /**
   * Disable the Reboot Time section
   */
  public setDisabledRebootTimeRadioGroup() {
    const antiTamperingCheckboxState = this.dom.$antiTamperingCheckboxForm.Input.checked;
    const callbackEvasionCheckboxState = this.dom.$callbackEvasionCheckboxForm.Input.checked;
    const rebootEndpointCheckboxState = this.dom.$rebootEndpointCheckboxForm.Input.checked;

    // disable radio group if anti-tampering, callback evasion or reboot endpoint checkboxes are disabled
    this.dom.$callbackEvasionRadioGroupForm.disabled = !antiTamperingCheckboxState
                                                       || !callbackEvasionCheckboxState
                                                       || !rebootEndpointCheckboxState;
  }

  private createFormGroup() {
    this.formGroup = new FormGroup({
      antiTamperingCheckboxForm: this.dom.$antiTamperingCheckboxForm,
      vulnerableDriversCheckboxForm: this.dom.$vulnerableDriversCheckboxForm,
      vulnerableDriversRadioGroupForm: this.dom.$vulnerableDriversRadioGroupForm,
      callbackEvasionCheckboxForm: this.dom.$callbackEvasionCheckboxForm,
      isolateEndpointCheckboxForm: this.dom.$isolateEndpointCheckboxForm,
      rebootEndpointCheckboxForm: this.dom.$rebootEndpointCheckboxForm,
      callbackEvasionRadioGroupForm: this.dom.$callbackEvasionRadioGroupForm,
    });

    if (Application.Main.hasProducts([
      LicenseProductIdEnum.ProductIdCeps,
      LicenseProductIdEnum.ProductIdOfficeSecurityPack,
      LicenseProductIdEnum.ProductIdBusinessSecurityPack,
    ])) {
      if (!this.dom.$isolateEndpointCheckboxForm.classList.contains('hidden')) {
        this.dom.$isolateEndpointCheckboxForm.classList.add('hidden');
      }
    } else {
      this.dom.$isolateEndpointCheckboxForm.classList.remove('hidden');
    }
  }

  private setFormData(data) {
    this.formGroup.value = {
      antiTamperingCheckboxForm: data.formValue.enabled,
      vulnerableDriversCheckboxForm: data.formValue.vulnerableDrivers.enabled,
      vulnerableDriversRadioGroupForm: data.formValue.vulnerableDrivers.action.toString(),
      callbackEvasionCheckboxForm: data.formValue.callbackEvasion.enabled,
      isolateEndpointCheckboxForm: data.formValue.callbackEvasion.actions.isolateEndpoint,
      rebootEndpointCheckboxForm: data.formValue.callbackEvasion.actions.rebootEndpoint,
      callbackEvasionRadioGroupForm: data.formValue.callbackEvasion.actions.rebootTime.toString(),
    };
  }
}
